import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { crmFetch } from './crmApi';
import { TASK_PRIORITIES, contactName } from './constants';

const EMPTY = { title: '', accountId: 'none', contactId: 'none', dueDate: '', priority: 'medium' };

export const TaskModal = ({ open, onOpenChange, onSaved, defaultAccountId }) => {
  const [accounts, setAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ ...EMPTY, accountId: defaultAccountId || 'none' });
    crmFetch('/accounts').then((r) => setAccounts(r.data || []));
  }, [open, defaultAccountId]);

  useEffect(() => {
    if (!open || form.accountId === 'none') { setContacts([]); return; }
    crmFetch(`/contacts?accountId=${form.accountId}`).then((r) => setContacts(r.data || []));
  }, [open, form.accountId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.title.trim()) { toast.error('Task title is required'); return; }
    setSaving(true);
    try {
      const res = await crmFetch('/tasks', {
        method: 'POST',
        body: JSON.stringify({
          title: form.title,
          accountId: form.accountId === 'none' ? null : form.accountId,
          contactId: form.contactId === 'none' ? null : form.contactId,
          dueDate: form.dueDate || null,
          priority: form.priority,
        }),
      });
      if (!res.success) { toast.error(res.message || 'Failed to create task'); return; }
      toast.success('Task created');
      onOpenChange(false);
      onSaved && onSaved(res.data);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="task-modal">
        <DialogHeader>
          <DialogTitle>New Task</DialogTitle>
          <DialogDescription>Create a follow-up task for yourself.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="mb-1.5 block">Task *</Label>
            <Input value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="e.g. Get feedback on Enterprise Architect" data-testid="task-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-1.5 block">Account</Label>
              <Select value={form.accountId} onValueChange={(v) => { set('accountId', v); set('contactId', 'none'); }}>
                <SelectTrigger data-testid="task-account-select"><SelectValue placeholder="Select account" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No account</SelectItem>
                  {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1.5 block">Contact</Label>
              <Select value={form.contactId} onValueChange={(v) => set('contactId', v)} disabled={form.accountId === 'none'}>
                <SelectTrigger data-testid="task-contact-select"><SelectValue placeholder="Select contact" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No contact</SelectItem>
                  {contacts.map((c) => <SelectItem key={c.id} value={c.id}>{contactName(c)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-1.5 block">Due Date</Label>
              <Input type="date" value={form.dueDate} onChange={(e) => set('dueDate', e.target.value)} data-testid="task-duedate-input" />
            </div>
            <div>
              <Label className="mb-1.5 block">Priority</Label>
              <Select value={form.priority} onValueChange={(v) => set('priority', v)}>
                <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TASK_PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="task-cancel-btn">Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="task-save-btn">
              {saving ? 'Saving…' : 'Create Task'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
